import { Component, EventEmitter, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Partner } from '../../../models/partner.model';
import { RequestComponent } from '../../request/request.component';
import { Country } from 'world-countries';
import { Region } from '../../../../model/region.model';
import { District } from '../../../../model/district.model';
import { ScreenSizeService } from '../../../../services/utils/screenSize.service';
import { PartnerService } from '../../../partners/clinic-control-panel/clinic-control-panel/services/partner.service';
import { ReviewService } from '../../../../services/review.service';
import { Review } from '../../../models/review.model';
import { Utils } from '../../../utils/utils';
import { PaginationInfo, TableUtilsService, ViewStatus } from '@uranda/ui';
import { OpenWriteReviewModalService } from '../../../../services/openWriteReviewModal.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalizedRouterService } from '../../../localized-router.service';
import { ModalOffcanvasService } from '../../../../services/modal-offcanvas.service';
import {Title} from "@angular/platform-browser";

@Component({
  selector: 'app-clinic-detail',
  templateUrl: './clinic-detail.component.html',
  styleUrls: ['./clinic-detail.component.scss'],
  standalone: false,
})
export class ClinicDetailComponent implements OnInit {
  protected Utils = Utils;
  partner?: Partner;
  title: string = '';
  description: string = '';
  partnerId?: string;
  reviews?: Review[];
  paginationInfo?: PaginationInfo;
  rating = 0;
  currentUrl: string = '';
  isOffcanvas = false;
  flagsSrcs?: { src: string; name: string }[];
  items: any;
  countries: Country[] = [];
  showButtonText = false;

  viewStatus = ViewStatus.LOADING;
  ViewStatus = ViewStatus;
  coverImageLoaded = false;
  reviewStats?: Review;
  // GET /v1/partners/{id} risponde 404 per cliniche inesistenti, sospese o non approvate:
  // invece di una pagina vuota mostriamo uno stato "clinica non disponibile".
  notFound = false;

  constructor(
    protected route: ActivatedRoute,
    protected modalService: NgbModal,
    protected offcanvasService: NgbOffcanvas,
    protected screenSize: ScreenSizeService,
    protected partnerService: PartnerService,
    protected reviewService: ReviewService,
    protected tableUtils: TableUtilsService,
    protected openWriteReviewModalService: OpenWriteReviewModalService,
    protected translateService: TranslateService,
    protected activatedRoute: ActivatedRoute,
    protected localizedRouter: LocalizedRouterService,
    protected modalOffcanvasService: ModalOffcanvasService,
    protected titleService:Title
  ) {
    this.route.params.subscribe(async (params) => {
      this.partnerId = params['partnerId'];
    });
    this.showButtonText = this.screenSize.showCompactButtons;
  }

  async ngOnInit() {
    this.currentUrl = window.location.href;
    console.log('Current URL:', this.currentUrl);
    if (this.partnerId) {
      await this.loadPartnerData();
    } else if (this.partner) {
      this.partnerId = String(this.partner.id);
      await this.init();
      this.viewStatus = ViewStatus.READY;
    }
  }

  async loadPartnerData() {
    this.viewStatus = ViewStatus.LOADING;
    this.coverImageLoaded = false;
    this.notFound = false;

    try {
      this.partner = await this.partnerService.getPartnerById(
        Number(this.partnerId)
      );
      this.titleService.setTitle('Dental Tour - ' + this.partner.name);
      console.log('Loaded partner data:', this.partner);
      await this.init();
    } catch (error) {
      console.error('Error loading partner data:', error);
      this.partner = undefined;
      this.notFound = true;
    } finally {
      this.viewStatus = ViewStatus.READY;
    }
  }

  onCoverImageLoad() {
    this.coverImageLoaded = true;
  }

  onCoverImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/dentalclinic.webp';
    this.coverImageLoaded = true;
  }

  async init() {
    try {
      const reviews = await this.reviewService.getReviewsByPartnerId(
        this.partnerId,
        undefined,
        undefined,
        0,
        this.screenSize.isDesktop ? 3 : 10
      );

      this.reviews = reviews.content;
      this.paginationInfo =
        this.tableUtils.convertPageResponseToPaginationInfo(reviews);

      if (this.partnerId) {
        try {
          this.reviewStats = await this.reviewService.getReviewStats(this.partnerId);
        } catch (error) {
          console.error('Error loading review stats:', error);
        }
      }

      this.flagsSrcs = this.partner?.languagesSpoken?.map((code) => ({
        src: Utils.getFlagSrc(code),
        name: Utils.getFlagName(code),
      }));

      this.title = `${this.partner?.name}`;
      this.description = this.translateService.instant('share.CHECK_PARTNER', {
        name: this.partner?.name ?? this.translateService.instant('our clinic'),
      });
    } catch (error) {
      console.error('Error in init:', error);
    }
  }

  openWriteReviewModal() {
    if (!this.partner) return;

    const partnerInfo = {
      id: this.partner.id,
      name: this.partner.name,
    };

    this.openWriteReviewModalService
      .openWriteReviewModal(partnerInfo, false, this.isOffcanvas)
      .then((result) => {
        if (result && result.success) {
          this.loadPartnerData();
        }
      });
  }

  openContactModal(): void {
    this.modalOffcanvasService.openComponent(
      RequestComponent,
      {
        scope: 'contact',
        clinicName: this.partner?.name,
        partnerId: this.partner?.id?.toString(),
        isOffcanvas: this.isOffcanvas || !this.screenSize.isDesktop,
      },
      { backdrop: 'static' },
      this.isOffcanvas || !this.screenSize.isDesktop
    );
  }

  goBackToSearch() {
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    this.localizedRouter.navigate(['search'], { queryParams });
  }

  get hasReviews(): boolean {
    return (this.partner?.reviewsCount ?? 0) > 0;
  }

  get reviewsLabel(): string {
    const count = this.partner?.reviewsCount ?? 0;
    const review = count === 1 ? 'review' : 'reviews';
    return `${count} ${this.translateService.instant(review)}`;
  }

  get showRating(): boolean {
    return this.hasReviews && (this.partner?.averageRate ?? 0) > 0;
  }
}
